import dayjs from 'dayjs';
import * as Yup from 'yup';

export const endDateValidation = Yup.date()
  .required()
  .when('start', ([start], schema) => {
    if (!start) {
      return schema;
    }

    const date = dayjs(start);
    if (!date.isValid()) {
      return schema;
    }

    return schema.min(
      date.startOf('d').toDate(),
      'The end date must be equal to or greater than the start date'
    );
  });

export const formatTimestampToDateTime = (timestamp: any) => {
  const date = new Date(timestamp);
  return date.toISOString().replace('T', ' ').substring(0, 19);
};

export const expandToOneMinuteSlots = (slots: any[]) => {
  const expandedSlots: any = [];

  slots.forEach((slot) => {
    // Normalize start and end times to timestamps (numbers)
    let startTime: number | null = null;
    let endTime: number | null = null;

    if (slot.startTime) {
      startTime =
        typeof slot.startTime === 'number'
          ? slot.startTime
          : new Date(slot.startTime).getTime();
    }

    if (slot.endTime) {
      endTime =
        typeof slot.endTime === 'number'
          ? slot.endTime
          : new Date(slot.endTime).getTime();
    }
    if (startTime && endTime && !isNaN(startTime) && !isNaN(endTime)) {
      let current = startTime;

      while (current < endTime) {
        expandedSlots.push({
          startTime: current,
          endTime: current + 60 * 1000,
          doctorNames: slot?.doctorNames || [],
        });

        current += 60 * 1000;
      }
    }
  });

  return expandedSlots;
};

/**
 * Splits slots by provider/resource. Groups slots where each slot contains only one provider.
 * @param slots - Array of slots with doctorNames array
 * @returns Array of slots, each with a single provider in doctorNames
 */
export const splitSlotsByProvider = (slots: any[]) => {
  const providerSlots: any[] = [];

  slots.forEach((slot) => {
    const { doctorNames = [], ...rest } = slot;

    doctorNames.forEach((doctor: any) => {
      providerSlots.push({
        ...rest,
        doctorNames: [doctor], // single provider
        providerId: doctor?.ProviderId,
        resourceId: doctor?.ProviderId,
      });
    });
  });

  return providerSlots;
};

/**
 * Groups slots by provider/resource ID
 * @param slots - Array of slots (should already be split by provider)
 * @returns Map of providerId -> slots array
 */
export const groupSlotsByProvider = (slots: any[]) => {
  const providerGroups = new Map<string, any[]>();

  slots.forEach((slot) => {
    const providerId =
      slot.providerId || slot.resourceId || slot.doctorNames?.[0]?.ProviderId;

    if (providerId) {
      if (!providerGroups.has(providerId)) {
        providerGroups.set(providerId, []);
      }
      providerGroups.get(providerId)!.push(slot);
    }
  });

  return providerGroups;
};

/**
 * Deduplicates slots for a provider. If multiple slots have the same start time,
 * picks the one that exists (has valid values).
 * @param slots - Array of slots for a single provider
 * @returns Deduplicated array of slots
 */
export const deduplicateSlotsByStartTime = (slots: any[]) => {
  const slotsByStartTime = new Map<number, any>();

  slots.forEach((slot) => {
    // Normalize start time to timestamp (number)
    let startTime: number | null = null;

    if (slot.startTime) {
      startTime =
        typeof slot.startTime === 'number'
          ? slot.startTime
          : new Date(slot.startTime).getTime();
    } else if (slot.start_time) {
      startTime =
        typeof slot.start_time === 'number'
          ? slot.start_time
          : new Date(slot.start_time).getTime();
    }

    if (startTime && !isNaN(startTime)) {
      const existingSlot = slotsByStartTime.get(startTime);

      // If no existing slot, use current slot
      if (!existingSlot) {
        slotsByStartTime.set(startTime, slot);
      } else {
        // If both slots exist, prefer the one with more complete data
        const currentHasValue =
          (slot.endTime || slot.end_time) && slot.doctorNames?.length;
        const existingHasValue =
          (existingSlot.endTime || existingSlot.end_time) &&
          existingSlot.doctorNames?.length;

        // Prefer slot that exists (has values)
        if (currentHasValue && !existingHasValue) {
          slotsByStartTime.set(startTime, slot);
        } else if (!currentHasValue && existingHasValue) {
          // Keep existing slot
        } else if (currentHasValue && existingHasValue) {
          // Both have values, prefer the one with more doctor names or better data
          if (slot.doctorNames?.length > existingSlot.doctorNames?.length) {
            slotsByStartTime.set(startTime, slot);
          }
        } else {
          // Neither has complete data, prefer the one with at least some data
          if (slot.doctorNames?.length && !existingSlot.doctorNames?.length) {
            slotsByStartTime.set(startTime, slot);
          }
        }
      }
    }
  });

  return Array.from(slotsByStartTime.values());
};

/**
 * Merges slots for a single provider based on duration requirements.
 * Expands slots to 1-minute intervals, then combines them into slots of the required duration.
 * @param slots - Array of slots for a single provider
 * @param duration - Required duration in minutes (e.g., 10, 30, 60)
 * @param currentSlotGap - Gap between slot checks in minutes (default: 10)
 * @returns Array of merged slots
 */
export const mergeSlotsForProvider = (
  slots: any[],
  duration: number,
  currentSlotGap: number = 10
) => {
  const mergedSlots: any[] = [];
  const availableSlots = new Map<number, any>();

  // Expand slots to 1-minute intervals
  const oneMinuteSlots = expandToOneMinuteSlots(slots);

  // Store 1-minute slots in a map
  oneMinuteSlots.forEach((slot: any) => {
    const startTime = slot.startTime;
    // If multiple slots exist for same start time, keep the one with doctorNames
    if (!availableSlots.has(startTime) || slot.doctorNames?.length) {
      availableSlots.set(startTime, slot);
    }
  });

  if (availableSlots.size === 0) {
    return mergedSlots;
  }

  // Find the time range
  let minTime = Infinity;
  let maxTime = -Infinity;

  availableSlots.forEach((_value, key) => {
    if (key < minTime) {
      minTime = key;
    }
    if (key > maxTime) {
      maxTime = key;
    }
  });

  const slotDurationMs = duration * 60 * 1000;
  const gapMs = currentSlotGap * 60 * 1000;
  let currentStartTime = minTime;

  // Try to create slots of the required duration
  while (currentStartTime + slotDurationMs <= maxTime) {
    let currentTime = currentStartTime;
    let valid = true;
    const doctorNamesSet = new Set<string>();
    const doctorNamesMap = new Map<string, any>();

    // Check if we have continuous availability for the required duration
    while (currentTime < currentStartTime + slotDurationMs) {
      const slot = availableSlots.get(currentTime);

      if (!slot || !slot.doctorNames?.length) {
        valid = false;
        break;
      }

      // Collect all doctor names for this time slot
      slot.doctorNames.forEach((doctor: any) => {
        const providerId = doctor.ProviderId;
        if (providerId) {
          doctorNamesSet.add(providerId);
          if (!doctorNamesMap.has(providerId)) {
            doctorNamesMap.set(providerId, doctor);
          }
        }
      });

      currentTime += gapMs; // Check at gap intervals (e.g., every 10 minutes)
    }

    if (valid && doctorNamesMap.size > 0) {
      mergedSlots.push({
        startTime: currentStartTime,
        endTime: currentStartTime + slotDurationMs,
        doctorNames: Array.from(doctorNamesMap.values()),
        start_time: new Date(currentStartTime).toISOString(),
        end_time: new Date(currentStartTime + slotDurationMs).toISOString(),
      });
    }

    // Move to next potential slot start (increment by duration)
    currentStartTime += slotDurationMs;
  }

  return mergedSlots;
};

/**
 * Main function to process slots: split by provider, deduplicate, merge per provider, and combine results.
 * @param slots - Array of slots with multiple providers
 * @param duration - Required duration in minutes (e.g., 10, 30, 60)
 * @param currentSlotGap - Gap between slot checks in minutes (default: 10)
 * @returns Array of merged slots for all providers
 */
export const mergeSlots = (
  slots: any[],
  duration: number,
  currentSlotGap: number = 10
) => {
  // Step 1: Split slots by provider (each slot will have one provider)
  const providerSlots = splitSlotsByProvider(slots);

  // Step 2: Group slots by provider
  const providerGroups = groupSlotsByProvider(providerSlots);

  // Step 3: Process each provider separately
  const allMergedSlots: any[] = [];

  providerGroups.forEach((providerSlotsArray) => {
    // Step 3a: Deduplicate slots for this provider (if same start time, pick the one that exists)
    const deduplicatedSlots = deduplicateSlotsByStartTime(providerSlotsArray);

    // Step 3b: Merge slots for this provider based on duration
    const mergedSlotsForProvider = mergeSlotsForProvider(
      deduplicatedSlots,
      duration,
      currentSlotGap
    );

    // Step 3c: Add merged slots to the result
    allMergedSlots.push(...mergedSlotsForProvider);
  });

  // Step 4: Return all merged slots
  return allMergedSlots;
};
